import { Component, inject, OnInit } from '@angular/core';
import {
  FormBuilder,
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { MatButton } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { tap } from 'rxjs';
import {
  InputFieldComponent,
  Item,
  SelectFieldComponent,
  SwitchFieldComponent,
} from '../../../form';
import { FieldTypeENUM, IFields } from '../../models';

/**
 * Interface representing the data structure passed into the manage field modal.
 */
export interface ManageFieldModalData {
  fieldData: IFields;
  availableFields: IFields[];
}

/**
 * The value emitted when the manage field modal closes after confirmation.
 */
export interface ManageFieldResult {
  required?: boolean | null;
  min?: number | null;
  max?: number | null;
  minCharts?: number | null;
  maxCharts?: number | null;
  hasCondition?: boolean | null;
  conditionFieldId?: Item | string | null;
  conditionValue?: Item | string | number | boolean | null;
}

@UntilDestroy()
@Component({
  selector: 'siat-manage-field',
  imports: [
    SwitchFieldComponent,
    InputFieldComponent,
    SelectFieldComponent,
    MatDialogModule,
    MatButton,
    ReactiveFormsModule,
  ],
  templateUrl: './manage-field.component.html',
})
export class ManageFieldComponent implements OnInit {
  /**
   * Data injected into the modal dialog containing field config and available fields.
   */
  modalData = inject<ManageFieldModalData>(MAT_DIALOG_DATA);

  /**
   * Reference to the currently active modal dialog.
   */
  activeModal = inject(MatDialogRef<ManageFieldComponent, ManageFieldResult>);

  /**
   * Form builder service instance.
   */
  formBuilder = inject(FormBuilder);

  /**
   * Enum mapping for field types used inside the template.
   */
  fieldTypeENUM = FieldTypeENUM;

  /**
   * List of other fields formatted as Items for the siat-select-field dropdown.
   */
  availableFieldsForCondition: Item[] = [];

  /**
   * List of options corresponding to the selected condition field (if applicable).
   */
  conditionValueOptions: Item[] = [];

  /**
   * Stores the currently selected parent field object to dynamically adapt the UI value input.
   */
  selectedConditionField: IFields | null = null;

  /**
   * The reactive form group managing the modal's internal state.
   */
  manageFieldForm = new FormGroup({
    required: new FormControl(false),
    min: new FormControl(),
    max: new FormControl(),
    minCharts: new FormControl(),
    maxCharts: new FormControl(),
    hasCondition: new FormControl(false),
    conditionFieldId: new FormControl<Item | string | null>(null),
    conditionValue: new FormControl<Item | string | number | boolean | null>(
      null
    ),
  });

  /**
   * Component initialization lifecycle hook.
   */
  ngOnInit(): void {
    const fieldData = this.modalData.fieldData;

    this.availableFieldsForCondition = this.initAvailableConditionFields();
    this.initCommonFields(fieldData);
    this.initTypeSpecificFields(fieldData);
    this.initConditionalLogic(fieldData);
    this.subscribeToConditionSwitch();
  }

  /**
   * @returns {Item[]} The mapped array of valid condition fields.
   */
  private initAvailableConditionFields(): Item[] {
    if (!this.modalData.availableFields) return [];

    const validFields: Item[] = [];

    this.modalData.availableFields.forEach(field => {
      const hasLabel = field.label && field.label.trim() !== '';
      const hasPlaceholder =
        field.placeholder && field.placeholder.trim() !== '';

      if (hasLabel || hasPlaceholder) {
        validFields.push({
          id: field.id || '',
          name: hasLabel ? field.label! : `[Placeholder] ${field.placeholder}`,
        });
      }
    });

    return validFields;
  }

  /**
   * Initializes properties common across all field types (e.g., required status).
   * @param {IFields} field The field data.
   */
  private initCommonFields(field: IFields): void {
    if (field.type === FieldTypeENUM.ACCORDION) {
      this.manageFieldForm
        .get('required')
        ?.setValue(false, { emitEvent: false });
      return;
    }

    if (field.required) {
      this.manageFieldForm
        .get('required')
        ?.setValue(true, { emitEvent: false });
    }
  }

  /**
   * Routes initialization to type-specific setups based on the field type.
   * @param {IFields} field The field data.
   */
  private initTypeSpecificFields(field: IFields): void {
    switch (field.type) {
      case FieldTypeENUM.NUMBER:
        this.manageFieldForm.patchValue(
          { min: field.min, max: field.max },
          { emitEvent: false }
        );
        break;
      case FieldTypeENUM.TEXT:
      case FieldTypeENUM.TEXTAREA:
        this.manageFieldForm.patchValue(
          { minCharts: field.min, maxCharts: field.max },
          { emitEvent: false }
        );
        break;
    }
  }

  /**
   * Initializes the conditional logic section, orchestrating data pre-fill and listeners.
   * @param {IFields} field The field data.
   */
  private initConditionalLogic(field: IFields): void {
    const hasCondition = !!field.hasCondition;

    this.manageFieldForm.patchValue(
      {
        hasCondition,
      },
      { emitEvent: false }
    );

    if (this.availableFieldsForCondition.length === 0) {
      this.manageFieldForm.get('hasCondition')?.disable({ emitEvent: false });
      return;
    }

    if (hasCondition) this.prefillExistingCondition(field);

    this.setupConditionFieldListener();
  }

  /**
   * Checks if existing condition data is valid and populates the form controls accordingly.
   * @param {IFields} field The field data containing existing condition info.
   */
  private prefillExistingCondition(field: IFields): void {
    if (!field.conditionFieldId) return;

    const isParentValid = this.isParentFieldValid(field.conditionFieldId);
    if (!isParentValid) return;

    this.updateSelectedConditionField(field.conditionFieldId);

    const isValueValid = this.isConditionValueValid(field.conditionValue);

    this.applyConditionPreFill(
      field.conditionFieldId,
      field.conditionValue,
      isValueValid
    );
  }

  /**
   * Verifies if the previously saved parent condition field still exists and is valid.
   * @param {string} conditionFieldId The ID of the parent condition field.
   * @returns {boolean} True if the parent field is valid.
   */
  private isParentFieldValid(conditionFieldId: string): boolean {
    return this.availableFieldsForCondition.some(
      f => f.id === conditionFieldId
    );
  }

  /**
   * Verifies if the previously saved condition value is still valid for the selected parent.
   * @param {string | number | boolean | undefined} conditionValue The value to check.
   * @returns {boolean} True if the value is valid.
   */
  private isConditionValueValid(
    conditionValue: string | number | boolean | undefined
  ): boolean {
    if (!this.selectedConditionField) return true;

    const condType = this.selectedConditionField.type;
    const isOptionBased =
      condType === FieldTypeENUM.SELECT ||
      condType === FieldTypeENUM.RADIOBUTTON ||
      condType === FieldTypeENUM.CHECKBOX ||
      condType === FieldTypeENUM.PRIORITY ||
      condType === FieldTypeENUM.NAZIONE ||
      condType === FieldTypeENUM.TOGGLE;

    if (!isOptionBased) return true;

    return this.conditionValueOptions.some(opt => opt.id === conditionValue);
  }

  /**
   * Applies the pre-fill values to the reactive form based on data validity.
   * @param {string} fieldId The parent field ID.
   * @param {string | number | boolean | undefined} value The condition value.
   * @param {boolean} isValueValid Determines if the value should be populated or cleared.
   */
  private applyConditionPreFill(
    fieldId: string,
    value: string | number | boolean | undefined,
    isValueValid: boolean
  ): void {
    const hasConditionCtrl = this.manageFieldForm.get('hasCondition');
    const conditionFieldIdCtrl = this.manageFieldForm.get('conditionFieldId');
    const conditionValueCtrl = this.manageFieldForm.get('conditionValue');

    hasConditionCtrl?.setValue(true, { emitEvent: false });
    conditionFieldIdCtrl?.setValue(fieldId, { emitEvent: false });

    if (isValueValid) {
      conditionValueCtrl?.setValue(value ?? null, { emitEvent: false });
    }
  }

  /**
   * Sets up the listener for when the user selects a new parent field from the dropdown.
   */
  private setupConditionFieldListener(): void {
    this.manageFieldForm
      .get('conditionFieldId')
      ?.valueChanges.pipe(
        untilDestroyed(this),
        tap((selectedFieldVal: Item | string | null) =>
          this.handleConditionFieldChange(selectedFieldVal)
        )
      )
      .subscribe();
  }

  /**
   * Handles the logic when the selected parent field changes.
   * @param {Item | string | null} selectedFieldVal The newly selected field value (can be string or object).
   */
  private handleConditionFieldChange(
    selectedFieldVal: Item | string | null
  ): void {
    const isFieldObject =
      !!selectedFieldVal && typeof selectedFieldVal === 'object';
    const fieldIdString: string | number | null = isFieldObject
      ? (selectedFieldVal as Item).id
      : (selectedFieldVal as string | null);

    if (this.selectedConditionField?.id !== fieldIdString) {
      this.updateSelectedConditionField(fieldIdString);
      this.manageFieldForm
        .get('conditionValue')
        ?.setValue(null, { emitEvent: false });
    }
  }

  /**
   * Listens to the condition toggle switch and dynamically updates the
   * validation requirements for the condition field and value, or clears them completely.
   */
  private subscribeToConditionSwitch(): void {
    this.manageFieldForm
      .get('hasCondition')
      ?.valueChanges.pipe(
        untilDestroyed(this),
        tap(isActive => {
          const fieldCtrl = this.manageFieldForm.get('conditionFieldId');
          const valCtrl = this.manageFieldForm.get('conditionValue');

          if (isActive) {
            fieldCtrl?.setValidators(Validators.required);
            valCtrl?.setValidators(Validators.required);
          } else {
            fieldCtrl?.clearValidators();
            valCtrl?.clearValidators();
            fieldCtrl?.setValue(null, { emitEvent: false });
            valCtrl?.setValue(null, { emitEvent: false });
            this.selectedConditionField = null;
            this.conditionValueOptions = [];
          }

          fieldCtrl?.updateValueAndValidity({ emitEvent: false });
          valCtrl?.updateValueAndValidity({ emitEvent: false });
        })
      )
      .subscribe();
  }

  /**
   * Finds the selected parent field from the original full data list and updates
   * the related properties to properly render the target value UI.
   * @param {string | number | null} fieldId The ID of the selected parent field.
   */
  private updateSelectedConditionField(
    fieldId: string | number | null
  ): void {
    this.selectedConditionField = fieldId
      ? this.modalData.availableFields.find(
          f => f.id === String(fieldId)
        ) || null
      : null;

    this.conditionValueOptions = this.extractConditionOptions(
      this.selectedConditionField
    );
  }

  /**
   * @returns {Item[]} A normalized array of valid options.
   */
  private extractConditionOptions(field: IFields | null): Item[] {
    if (!field) return [];

    if (field.type === FieldTypeENUM.PRIORITY) {
      return [
        { id: 1, name: 'Alta' },
        { id: 2, name: 'Media' },
        { id: 3, name: 'Bassa' },
      ];
    }
    if (field.type === FieldTypeENUM.TOGGLE) {
      return [
        { id: 'true', name: 'Sì' },
        { id: 'false', name: 'No' },
      ];
    }
    return (field.options || [])
      .map(opt => ({
        id: opt.id || '',
        name: (opt.name || opt.label || '').trim(),
      }))
      .filter(opt => opt.name !== '');
  }

  /**
   * Closes the modal without saving or emitting data to the parent component.
   */
  closeModal(): void {
    this.activeModal.close();
  }

  /**
   * Confirms the configuration, ensures the form is valid,
   * and closes the modal returning the form data.
   */
  confirmAndCloseModal(): void {
    if (this.manageFieldForm.valid) {
      const formValue = {
        ...this.manageFieldForm.value,
        required:
          this.modalData.fieldData.type === FieldTypeENUM.ACCORDION
            ? false
            : this.manageFieldForm.value.required,
      };

      this.activeModal.close(formValue);
    }
  }
}
